/**
 * AuditLogsPage — tenant-scoped audit trail.
 *
 * Shows every create / update / delete / login action recorded for this
 * tenant, filterable by date range, resource type, and action verb.
 * Data comes from GET /api/v1/audit/logs (requires auth; tenant-scoped by
 * the backend via the JWT).
 */
import { useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Shield, RefreshCw, ChevronDown, ChevronUp, Search } from 'lucide-react'
import { auditApi, type AuditEntry } from '../lib/api'

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:      'var(--color-bg)',
  card:    'var(--color-bg-card)',
  card2:   'var(--color-bg-card-2)',
  border:  'var(--color-border)',
  primary: 'var(--color-primary)',
  text:    'var(--color-text)',
  muted:   'var(--color-text-muted)',
  faint:   'var(--color-text-faint)',
  danger:  'var(--color-danger)',
  success: 'var(--color-success)',
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const ACTION_COLORS: Record<string, string> = {
  create: '#4CAF50',
  update: '#60A5FA',
  delete: '#F87171',
  login:  '#A78BFA',
  logout: '#6B7280',
  export: '#F5A623',
  import: '#F5A623',
}

function actionColor(action: string) {
  const verb = action.split('.')[0].toLowerCase()
  return ACTION_COLORS[verb] ?? C.faint
}

function actionLabel(action: string) {
  return action.replace(/_/g, ' ').replace(/\./g, ' › ')
}

function resourceTypeLabel(rt: string) {
  return rt.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function relativeTime(iso: string) {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60_000)
  if (m < 1)  return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d < 7)  return `${d}d ago`
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })
}

// ── Filters bar ───────────────────────────────────────────────────────────────

interface Filters {
  since: string
  until: string
  resource_type: string
  action: string
}

const RESOURCE_TYPES = [
  '', 'plant', 'harvest', 'variety', 'vendor', 'care_event', 'irrigation_zone',
  'inventory_item', 'propagation_batch', 'user', 'settings', 'integration',
]
const ACTIONS = ['', 'create', 'update', 'delete', 'login', 'logout', 'export']

const selStyle: React.CSSProperties = {
  background: C.card, border: `1px solid ${C.border}`, borderRadius: 7,
  padding: '6px 10px', color: C.text, fontSize: 12, cursor: 'pointer', outline: 'none',
}
const inpStyle: React.CSSProperties = {
  background: C.card, border: `1px solid ${C.border}`, borderRadius: 7,
  padding: '6px 10px', color: C.text, fontSize: 12, outline: 'none',
}

// ── Entry row ─────────────────────────────────────────────────────────────────

function EntryRow({ entry }: { entry: AuditEntry }) {
  const [open, setOpen] = useState(false)
  const hasChanges = entry.changes && Object.keys(entry.changes).length > 0
  const color = actionColor(entry.action)

  return (
    <div style={{ borderBottom: `1px solid ${C.border}` }}>
      <div
        onClick={() => hasChanges && setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', cursor: hasChanges ? 'pointer' : 'default' }}
      >
        {/* Action badge */}
        <span style={{
          flexShrink: 0, display: 'inline-flex', alignItems: 'center',
          padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600,
          background: color + '1A', color, border: `1px solid ${color}33`,
          minWidth: 68, justifyContent: 'center',
        }}>
          {entry.action.split('.')[0]}
        </span>

        {/* Resource */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
            {resourceTypeLabel(entry.resource_type)}
          </span>
          {entry.resource_label && (
            <span style={{ fontSize: 12, color: C.muted, marginLeft: 6 }}>
              — {entry.resource_label}
            </span>
          )}
          {entry.action.includes('.') && (
            <span style={{ fontSize: 11, color: C.faint, marginLeft: 6 }}>
              ({actionLabel(entry.action.split('.').slice(1).join('.'))})
            </span>
          )}
        </div>

        {/* Actor */}
        <div style={{ fontSize: 11, color: C.muted, flexShrink: 0, textAlign: 'right' }}>
          {entry.actor_name ?? entry.actor_email ?? 'System'}
        </div>

        {/* Time */}
        <div style={{ fontSize: 11, color: C.faint, flexShrink: 0, minWidth: 72, textAlign: 'right' }}>
          {relativeTime(entry.occurred_at)}
        </div>

        {/* Expand toggle */}
        {hasChanges
          ? (open ? <ChevronUp size={14} style={{ color: C.faint, flexShrink: 0 }} />
                  : <ChevronDown size={14} style={{ color: C.faint, flexShrink: 0 }} />)
          : <span style={{ width: 14, flexShrink: 0 }} />}
      </div>

      {/* Changes diff */}
      {open && hasChanges && (
        <div style={{ padding: '0 16px 12px 16px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                {['Field', 'Before', 'After'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '4px 8px', color: C.faint, fontWeight: 600, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', background: C.card2 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(entry.changes!).map(([field, { before, after }]) => (
                <tr key={field} style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={{ padding: '5px 8px', color: C.muted, fontWeight: 600, whiteSpace: 'nowrap' }}>{field.replace(/_/g, ' ')}</td>
                  <td style={{ padding: '5px 8px', color: '#F87171', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {before == null ? <span style={{ color: C.faint, fontStyle: 'italic' }}>—</span> : JSON.stringify(before)}
                  </td>
                  <td style={{ padding: '5px 8px', color: '#4CAF50', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {after == null ? <span style={{ color: C.faint, fontStyle: 'italic' }}>—</span> : JSON.stringify(after)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function AuditLogsPage() {
  const [filters, setFilters] = useState<Filters>({
    since: '', until: '', resource_type: '', action: '',
  })
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const setF = <K extends keyof Filters>(k: K, v: string) => {
    setFilters(f => ({ ...f, [k]: v }))
    setCursor(undefined) // reset pagination on filter change
  }

  const params = useCallback(() => ({
    ...(filters.since         && { since: new Date(filters.since + 'T00:00:00').toISOString() }),
    ...(filters.until         && { until: new Date(filters.until + 'T23:59:59').toISOString() }),
    ...(filters.resource_type && { resource_type: filters.resource_type }),
    ...(filters.action        && { action: filters.action }),
    ...(cursor                && { cursor }),
    limit: 50,
  }), [filters, cursor])

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['audit-logs', filters, cursor],
    queryFn: () => auditApi.listLogs(params()).then(r => r.data),
    staleTime: 30_000,
  })

  const entries = data?.entries ?? []
  const total   = data?.total
  const nextCursor = data?.next_cursor

  return (
    <div style={{ padding: '28px 32px', minHeight: '100%', background: C.bg }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <Shield size={20} style={{ color: C.primary }} />
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text, fontFamily: '"Syne", system-ui, sans-serif' }}>
              Audit Log
            </h1>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: C.muted }}>
            Every change made to your farm data — who did what and when
            {total != null && <span style={{ color: C.faint }}> · {total.toLocaleString()} total entries</span>}
          </p>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isLoading}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, background: C.card, border: `1px solid ${C.border}`, color: C.muted, cursor: 'pointer', fontSize: 13 }}
        >
          <RefreshCw size={13} style={{ animation: isLoading ? 'spin 1s linear infinite' : 'none' }} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        <Search size={13} style={{ color: C.faint }} />
        <input
          style={{ ...inpStyle, minWidth: 130 }}
          type="date"
          value={filters.since}
          onChange={e => setF('since', e.target.value)}
          title="From date"
          placeholder="From"
        />
        <span style={{ color: C.faint, fontSize: 12 }}>→</span>
        <input
          style={{ ...inpStyle, minWidth: 130 }}
          type="date"
          value={filters.until}
          onChange={e => setF('until', e.target.value)}
          title="To date"
        />
        <select style={selStyle} value={filters.resource_type} onChange={e => setF('resource_type', e.target.value)}>
          <option value="">All resource types</option>
          {RESOURCE_TYPES.filter(Boolean).map(rt => (
            <option key={rt} value={rt}>{resourceTypeLabel(rt)}</option>
          ))}
        </select>
        <select style={selStyle} value={filters.action} onChange={e => setF('action', e.target.value)}>
          <option value="">All actions</option>
          {ACTIONS.filter(Boolean).map(a => (
            <option key={a} value={a}>{a.charAt(0).toUpperCase() + a.slice(1)}</option>
          ))}
        </select>
        {(filters.since || filters.until || filters.resource_type || filters.action) && (
          <button
            onClick={() => { setFilters({ since: '', until: '', resource_type: '', action: '' }); setCursor(undefined) }}
            style={{ ...selStyle, color: C.danger, borderColor: C.danger + '44', background: C.danger + '11' }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Log table */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>

        {/* Column headers */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', borderBottom: `1px solid ${C.border}`, background: C.card2 }}>
          {['Action', 'Resource', 'Actor', 'Time', ''].map((h, i) => (
            <span key={i} style={{
              fontSize: 10, fontWeight: 700, color: C.faint, textTransform: 'uppercase', letterSpacing: '0.07em',
              flex: h === 'Resource' ? 1 : h === '' ? 0 : undefined,
              flexShrink: h === 'Resource' ? 1 : 0,
              minWidth: h === 'Action' ? 80 : h === 'Actor' ? 100 : h === 'Time' ? 72 : undefined,
            }}>
              {h}
            </span>
          ))}
        </div>

        {isLoading ? (
          <div style={{ padding: '60px 0', textAlign: 'center', color: C.faint, fontSize: 13 }}>Loading…</div>
        ) : isError ? (
          <div style={{ padding: '40px 24px', textAlign: 'center' }}>
            <p style={{ color: C.danger, fontSize: 14, margin: '0 0 6px' }}>Unable to load audit logs</p>
            <p style={{ color: C.faint, fontSize: 12, margin: 0 }}>
              The audit log endpoint (<code style={{ fontFamily: 'monospace' }}>GET /api/v1/audit/logs</code>) may not be implemented yet on the backend.
            </p>
          </div>
        ) : entries.length === 0 ? (
          <div style={{ padding: '60px 0', textAlign: 'center' }}>
            <Shield size={32} style={{ color: C.faint, opacity: 0.4, marginBottom: 12 }} />
            <p style={{ fontSize: 14, color: C.muted, margin: 0 }}>No audit entries found</p>
            <p style={{ fontSize: 12, color: C.faint, margin: '6px 0 0' }}>
              {filters.since || filters.until || filters.resource_type || filters.action
                ? 'Try adjusting your filters.'
                : 'Audit entries appear here as your team makes changes.'}
            </p>
          </div>
        ) : (
          entries.map(entry => <EntryRow key={entry.id} entry={entry} />)
        )}
      </div>

      {/* Pagination */}
      {(nextCursor || cursor) && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
          {cursor ? (
            <button onClick={() => setCursor(undefined)} style={{ ...selStyle, cursor: 'pointer' }}>
              ← Newer
            </button>
          ) : <span />}
          {nextCursor && (
            <button onClick={() => setCursor(nextCursor)} style={{ ...selStyle, cursor: 'pointer' }}>
              Older →
            </button>
          )}
        </div>
      )}

      {/* Legend */}
      <div style={{ marginTop: 20, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {Object.entries(ACTION_COLORS).map(([action, color]) => (
          <span key={action} style={{ fontSize: 11, color: C.faint, display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
            {action}
          </span>
        ))}
      </div>
    </div>
  )
}
